import Box from '@redbubble/design-system/src/Box';
import Text from '@redbubble/design-system/src/Text';
import { defineMessages, useIntl } from '@redbubble/next/intl';
import { UserInfo } from '@redbubble/next/types';
import { usePageProps, useUserInfo } from '@redbubble/next-provider';
import { localizedPath } from '../../../../utils/common';
import { ArtistShopData, NoResultsData } from '../../types';
import { ArtistDetails } from '../ArtistDetails';
import { RelatedTopicsList } from '../RelatedTopicsList';
import { SearchHeader } from '../SearchHeader';
import { SuggestedProductsCarousel } from '../SuggestedProductsCarousel/SuggestedProductsCarousel';

interface NoSearchResultsProps {
  searchTerm: string;
}

const messages = defineMessages({
  bubbleHeading: {
    defaultMessage: 'Try one of these popular searches',
  },
  noSearchResultsTitle: {
    defaultMessage: 'Nothing matches your search for "{searchTerm}"',
  },
  noSearchResultsDescription: {
    defaultMessage: "Don't give up! Check your spelling, or try something new.",
  },
  artistFoundHeading: {
    defaultMessage:
      'We found a person that matched "{artistName}", but not any works.',
  },
});

export const NoSearchResults: React.FC<NoSearchResultsProps> = ({
  searchTerm,
}) => {
  const { noResultsData, artistShopData } = usePageProps<{
    noResultsData: NoResultsData | null;
    artistShopData: ArtistShopData;
  }>();
  const intl = useIntl();
  const [{ locale }] = useUserInfo<UserInfo>();
  const headerText = {
    title: intl.formatMessage(messages.noSearchResultsTitle, { searchTerm }),
    pitch: intl.formatMessage(messages.noSearchResultsDescription),
  };

  const artistFoundHeaderText = {
    title: intl.formatMessage(messages.artistFoundHeading, {
      artistName: searchTerm,
    }),
  };

  const username = artistShopData?.username;
  const artistShopUrl =
    username && localizedPath(locale, `/people/${username}/shop`);

  const showArtistCard =
    artistShopUrl && artistShopData?.accountState === 'ACTIVE';

  const seoVisible = locale === 'en';

  return (
    <>
      <SearchHeader
        heroData={showArtistCard ? artistFoundHeaderText : headerText}
        title={null}
      />
      {showArtistCard && (
        <ArtistDetails
          shopUrl={artistShopUrl}
          shopDisplayName={artistShopData.shopDisplayName}
          additionalInfo={artistShopData.additionalInfo}
          avatar={artistShopData.avatar}
        />
      )}
      {noResultsData?.bubblesData && (
        <Box paddingTop="m">
          <Text type="display5" display="block" marginBottom="m">
            {intl.formatMessage(messages.bubbleHeading)}
          </Text>
          <RelatedTopicsList
            topics={noResultsData.bubblesData.bubbles}
            seoVisible={seoVisible}
          />
        </Box>
      )}

      {noResultsData?.suggestedResults && (
        <SuggestedProductsCarousel
          suggestedProducts={noResultsData.suggestedResults}
        />
      )}
    </>
  );
};
